/**
 * PlanRequiredHost — the ONE global surface for a server plan-feature refusal (S8).
 *
 * Mounted once in the shell next to CreditGuardHost. The API client fires
 * `psn:plan-required` on ANY 403 `feature_not_in_plan` / `plan_required`
 * (lib/api/plan-gate.ts); this host opens an upgrade dialog naming the feature,
 * with a « Voir les offres » CTA to the server-provided upgrade URL — instead of
 * the call site's generic "erreur" text. Shown once per feature per session (a
 * page that fires several gated calls does not stack dialogs).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { PLAN_REQUIRED_EVENT, type PlanRequired } from '@/lib/api/plan-gate';
import { isSuperadminCached } from './useEntitlement';
import { Modal } from '@/ui';
import { useT } from '@/i18n';

function prettify(key: string): string {
  const s = key.replace(/[_.-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}

export function PlanRequiredHost() {
  const { t } = useT();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PlanRequired | null>(null);
  const shown = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onRequired = (e: Event) => {
      const info = (e as CustomEvent<PlanRequired>).detail;
      if (!info || shown.current.has(info.feature)) return;
      // The platform superadmin is never offered an upgrade (full plan access).
      if (isSuperadminCached()) return;
      shown.current.add(info.feature);
      setPending(info);
    };
    window.addEventListener(PLAN_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(PLAN_REQUIRED_EVENT, onRequired);
  }, []);

  if (!pending) return null;
  const label = prettify(pending.feature);
  const close = () => setPending(null);

  return (
    <Modal
      title={t('shell.features.entitlementsPlanRequiredHost.featureNotIncludedInYourPlan')}
      size="md"
      onClose={close}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            className="rounded-token border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2"
          >{t('shell.features.entitlementsPlanRequiredHost.later')}</button>
          <button
            type="button"
            onClick={() => {
              close();
              navigate(pending.upgradeUrl);
            }}
            className="rounded-token bg-gradient-to-br from-primary to-accent px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-elevation-1 transition hover:brightness-110"
          >{t('shell.features.entitlementsPlanRequiredHost.viewThePlans')}</button>
        </>
      }
    >
      <div className="space-y-2 text-sm" data-testid="plan-required-dialog" data-feature={pending.feature}>
        <p className="text-foreground">
          « <span className="font-medium">{label}</span>{' '}{t('shell.features.entitlementsPlanRequiredHost.isNotIncludedInYourPlan')}{pending.planCode ? (
            <>
              {' '}
              (<span className="font-mono">{pending.planCode}</span>)
            </>
          ) : null}
          .
        </p>
        <p className="text-muted">
          {pending.detail ?? t('shell.features.entitlementsPlanRequiredHost.upgradeToAHigherPlanTo')}
        </p>
      </div>
    </Modal>
  );
}
